import { supabase } from '../lib/supabase';


export const workspaceService = {
  async createWorkspace(workspaceData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Generate slug on client side as fallback (trigger will handle if not provided)
    const slug = workspaceData?.name
      ?.toLowerCase()
      ?.replace(/[^a-z0-9]+/g, '-')
      ?.replace(/^-+|-+$/g, '');

    const { data, error } = await supabase?.from('workspaces')?.insert({
        name: workspaceData?.name,
        slug: slug,  // Provide slug to help with uniqueness
        team_size: workspaceData?.teamSize,
        industry_category: workspaceData?.industryCategory,
        primary_use_case: workspaceData?.primaryUseCase,
        description: workspaceData?.description,
        owner_id: user?.id
      })?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      name: data?.name,
      slug: data?.slug,
      teamSize: data?.team_size,
      industryCategory: data?.industry_category,
      primaryUseCase: data?.primary_use_case,
      description: data?.description,
      ownerId: data?.owner_id,
      createdAt: data?.created_at
    };
  },

  async getUserWorkspaces(userId) {
    const { data, error } = await supabase?.from('workspace_members')?.select(`
        workspace:workspaces (
          id,
          name,
          slug,
          team_size,
          industry_category,
          primary_use_case,
          description,
          owner_id,
          project_count,
          team_member_count,
          created_at,
          updated_at
        )
      `)?.eq('user_id', userId);

    if (error) throw error;

    return data?.map(item => ({
      id: item?.workspace?.id,
      name: item?.workspace?.name,
      slug: item?.workspace?.slug,
      teamSize: item?.workspace?.team_size,
      industryCategory: item?.workspace?.industry_category,
      primaryUseCase: item?.workspace?.primary_use_case,
      description: item?.workspace?.description,
      ownerId: item?.workspace?.owner_id,
      projectCount: item?.workspace?.project_count,
      teamMemberCount: item?.workspace?.team_member_count,
      createdAt: item?.workspace?.created_at,
      updatedAt: item?.workspace?.updated_at
    }));
  },

  async getWorkspaceById(workspaceId) {
    const { data, error } = await supabase?.from('workspaces')?.select('*')?.eq('id', workspaceId)?.single();

    if (error) throw error;

    return {
      id: data?.id,
      name: data?.name,
      slug: data?.slug,
      teamSize: data?.team_size,
      industryCategory: data?.industry_category,
      primaryUseCase: data?.primary_use_case,
      description: data?.description,
      ownerId: data?.owner_id,
      projectCount: data?.project_count,
      teamMemberCount: data?.team_member_count,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  async updateWorkspace(workspaceId, updates) {
    const dbUpdates = {};
    
    if (updates?.name !== undefined) dbUpdates.name = updates?.name;
    if (updates?.teamSize !== undefined) dbUpdates.team_size = updates?.teamSize;
    if (updates?.industryCategory !== undefined) dbUpdates.industry_category = updates?.industryCategory;
    if (updates?.primaryUseCase !== undefined) dbUpdates.primary_use_case = updates?.primaryUseCase;
    if (updates?.description !== undefined) dbUpdates.description = updates?.description;

    const { data, error } = await supabase?.from('workspaces')?.update(dbUpdates)?.eq('id', workspaceId)?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      name: data?.name,
      slug: data?.slug,
      teamSize: data?.team_size,
      industryCategory: data?.industry_category,
      primaryUseCase: data?.primary_use_case,
      description: data?.description,
      ownerId: data?.owner_id,
      projectCount: data?.project_count,
      teamMemberCount: data?.team_member_count,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  async deleteWorkspace(workspaceId) {
    const { error } = await supabase?.from('workspaces')?.delete()?.eq('id', workspaceId);

    if (error) throw error;
  },

  /**
   * Subscribe to workspace changes in real-time
   * @param {string} workspaceId - Workspace ID to monitor
   * @param {Function} onUpdate - Callback when workspace is updated
   * @returns {Function} - Unsubscribe function
   */
  subscribeToWorkspace(workspaceId, onUpdate) {
    const channel = supabase?.channel(`workspace_${workspaceId}`)?.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'workspaces',
          filter: `id=eq.${workspaceId}`
        },
        (payload) => {
          const updatedWorkspace = {
            id: payload?.new?.id,
            name: payload?.new?.name,
            slug: payload?.new?.slug,
            teamSize: payload?.new?.team_size,
            industryCategory: payload?.new?.industry_category,
            primaryUseCase: payload?.new?.primary_use_case,
            description: payload?.new?.description,
            ownerId: payload?.new?.owner_id,
            projectCount: payload?.new?.project_count,
            teamMemberCount: payload?.new?.team_member_count,
            createdAt: payload?.new?.created_at,
            updatedAt: payload?.new?.updated_at
          };
          onUpdate(updatedWorkspace);
        }
      )?.subscribe();

    return () => supabase?.removeChannel(channel);
  },

  /**
   * Subscribe to all user's workspaces changes
   * @param {string} userId - User ID
   * @param {Function} onChange - Callback for any workspace change
   * @returns {Function} - Unsubscribe function
   */
  subscribeToUserWorkspaces(userId, onChange) {
    // Subscribe to workspace_members to catch when user is added/removed from workspaces
    const membersChannel = supabase?.channel(`user_workspace_members_${userId}`)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'workspace_members',
          filter: `user_id=eq.${userId}`
        },
        (payload) => {
          onChange({ type: 'membership', payload });
        }
      )?.subscribe();

    // Subscribe to workspaces table for updates
    const workspacesChannel = supabase?.channel(`user_workspaces_${userId}`)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'workspaces'
        },
        (payload) => {
          onChange({ type: 'workspace', payload });
        }
      )?.subscribe();

    return () => {
      supabase?.removeChannel(membersChannel);
      supabase?.removeChannel(workspacesChannel);
    };
  },

  /**
   * Subscribe to team member changes
   * @param {string} workspaceId - Workspace ID
   * @param {Function} onChange - Callback for member changes
   * @returns {Function} - Unsubscribe function
   */
  subscribeToTeamMembers(workspaceId, onChange) {
    const channel = supabase?.channel(`workspace_members_${workspaceId}`)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'workspace_members',
          filter: `workspace_id=eq.${workspaceId}`
        },
        (payload) => {
          onChange(payload);
        }
      )?.subscribe();

    return () => supabase?.removeChannel(channel);
  },

  /**
   * Subscribe to workspace invitations
   * @param {string} workspaceId - Workspace ID
   * @param {Function} onChange - Callback for invitation changes
   * @returns {Function} - Unsubscribe function
   */
  subscribeToInvitations(workspaceId, onChange) {
    const channel = supabase?.channel(`workspace_invitations_${workspaceId}`)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'workspace_invitations',
          filter: `workspace_id=eq.${workspaceId}`
        },
        (payload) => {
          onChange(payload);
        }
      )?.subscribe();

    return () => supabase?.removeChannel(channel);
  }
};